
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { GameHistory } from "@/components/GameHistory";
import { GamePerformanceChart } from "@/components/GamePerformanceChart";
import { UserMenu } from "@/components/UserMenu";
import { useToast } from "@/components/ui/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { createNewGame, getDailyGame, checkUserCompletedDailyChallenge, getTimeUntilNextDailyChallenge } from "@/utils/game";
import { Calendar, Target, User, Bot } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import Footer from "@/components/Footer";

export default function Index() {
  const navigate = useNavigate();
  const {
    toast
  } = useToast();
  const {
    user
  } = useAuth();
  const [isCreatingGame, setIsCreatingGame] = useState(false);
  const [isCreatingDailyGame, setIsCreatingDailyGame] = useState(false);
  const [dailyGameAvailable, setDailyGameAvailable] = useState(false);
  const [dailyGameId, setDailyGameId] = useState<string | null>(null);
  const [hasCompletedDailyChallenge, setHasCompletedDailyChallenge] = useState(false);
  const [timeUntilNextChallenge, setTimeUntilNextChallenge] = useState<{
    hours: number;
    minutes: number;
    seconds: number;
    totalSeconds: number;
  } | null>(null);

  useEffect(() => {
    const checkDailyGame = async () => {
      try {
        const dailyGame = await getDailyGame();
        if (dailyGame) {
          setDailyGameAvailable(true);
          setDailyGameId(dailyGame.id);

          if (user?.id) {
            const completed = await checkUserCompletedDailyChallenge(user.id, dailyGame.id);
            setHasCompletedDailyChallenge(completed);
            if (completed) {
              setTimeUntilNextChallenge(getTimeUntilNextDailyChallenge());
            }
          }
        } else {
          setDailyGameAvailable(false);
          setDailyGameId(null);
        }
      } catch (error) {
        console.error("Error checking daily game:", error);
      }
    };
    checkDailyGame();
  }, [user]);

  useEffect(() => {
    if (!hasCompletedDailyChallenge) return;
    const timerInterval = setInterval(() => {
      setTimeUntilNextChallenge(getTimeUntilNextDailyChallenge());
    }, 1000);
    return () => clearInterval(timerInterval);
  }, [hasCompletedDailyChallenge]);

  useEffect(() => {
    if (timeUntilNextChallenge && timeUntilNextChallenge.totalSeconds <= 0) {
      setHasCompletedDailyChallenge(false);
      setTimeUntilNextChallenge(null);
      getDailyGame().then(dailyGame => {
        if (dailyGame) {
          setDailyGameId(dailyGame.id);
          setDailyGameAvailable(true);
        }
      });
    }
  }, [timeUntilNextChallenge]);

  const handleNewGame = async () => {
    if (!user) {
      navigate('/login');
      toast({
        title: "Authentication required",
        description: "Sign in to play personal games and track your results.",
      });
      return;
    }
    
    try {
      setIsCreatingGame(true);
      const {
        gameId
      } = await createNewGame(user.id);
      navigate(`/game?id=${gameId}`);
    } catch (error) {
      console.error("Error creating game:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "There was a problem creating a new game."
      });
    } finally {
      setIsCreatingGame(false);
    }
  };

  const handleDailyChallenge = async () => {
    if (!dailyGameId) {
      toast({
        variant: "destructive",
        title: "No daily challenge",
        description: "There is no daily challenge available today."
      });
      return;
    }
    
    try {
      setIsCreatingDailyGame(true);
      const { gameId } = await createNewGame(user?.id || null, 'Daily', dailyGameId);
      navigate(`/game?id=${gameId}&type=daily`);
    } catch (error) {
      console.error("Error creating daily game:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "There was a problem starting the daily challenge."
      });
    } finally {
      setIsCreatingDailyGame(false);
    }
  };

  const formatCountdown = () => {
    if (!timeUntilNextChallenge) return "";
    const {
      hours,
      minutes,
      seconds
    } = timeUntilNextChallenge;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  return <div className="min-h-screen flex flex-col bg-background">
      <header className="w-full border-b border-border fixed top-0 z-10 bg-background">
        <div className="container mx-auto py-4 px-4 flex justify-between items-center">
          <h1 className="text-xl font-semibold text-foreground flex items-center gap-2">
            <img src="/lovable-uploads/82002023-ce31-46c5-b6ce-14f9cdbffeaa.png" alt="Convergence Logo" className="w-8 h-8" />
            Convergence
          </h1>
          <UserMenu />
        </div>
      </header>
      
      <main className="container mx-auto flex flex-col items-center justify-start py-16 px-4 flex-1 mt-14">
        <div className="w-full max-w-2xl animate-fadeIn">
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-foreground mb-4">Welcome to Convergence</h1>
            <p className="text-muted-foreground max-w-md mx-auto mb-8">How many rounds does it take to converge with the AI bot?</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              {dailyGameAvailable && <Button 
                size="lg" 
                variant="default" 
                onClick={handleDailyChallenge} 
                disabled={isCreatingDailyGame || hasCompletedDailyChallenge} 
                className="min-w-[200px] w-full sm:w-[250px] text-center"
              >
                <Calendar className="mr-2 h-4 w-4 flex-shrink-0" />
                <span className="whitespace-nowrap overflow-hidden text-ellipsis">
                  {isCreatingDailyGame ? "Starting..." : hasCompletedDailyChallenge ? `Next daily in ${formatCountdown()}` : "Daily Challenge"}
                </span>
              </Button>}
              
              <Button 
                size="lg" 
                variant="secondary" 
                onClick={handleNewGame} 
                disabled={isCreatingGame} 
                className="min-w-[200px]"
              >
                {isCreatingGame ? "Creating..." : "Personal Game"}
              </Button>
            </div>
          </div>
          
          {user ? <div className="mt-12">
              <Tabs defaultValue="stats" className="w-full">
                <div className="border-b mb-6">
                  <TabsList className="bg-transparent p-0 h-auto">
                    <TabsTrigger value="stats" className="text-lg pb-2 px-4 rounded-none data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:text-foreground data-[state=active]:border-b-2 data-[state=active]:border-primary">
                      My Stats
                    </TabsTrigger>
                    <TabsTrigger value="games" className="text-lg pb-2 px-4 rounded-none data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:text-foreground data-[state=active]:border-b-2 data-[state=active]:border-primary">
                      My Games
                    </TabsTrigger>
                  </TabsList>
                </div>
                <TabsContent value="stats" className="mt-0">
                  <GamePerformanceChart />
                </TabsContent>
                <TabsContent value="games" className="mt-0">
                  <GameHistory />
                </TabsContent>
              </Tabs>
            </div> : <div className="mt-12 space-y-8">
              <h3 className="text-2xl font-medium text-center">How to Play</h3>
              
              <Card>
                <CardContent className="p-6">
                  <h4 className="text-lg font-medium mb-4">1. Pick Words</h4>
                  <p className="text-muted-foreground mb-4">Each round, you and the bot each pick a word or name.</p>
                  
                  <div className="grid grid-cols-2 gap-4 mt-6 bg-muted/30 p-4 rounded-md">
                    <div className="p-3 bg-card rounded shadow-sm border border-border">
                      <div className="text-xs text-muted-foreground mb-2 text-center flex items-center justify-center gap-1.5">
                        <User className="w-4 h-4" />
                      </div>
                      <div className="text-center">boat</div>
                    </div>
                    <div className="p-3 bg-card rounded shadow-sm border border-border">
                      <div className="text-xs text-muted-foreground mb-2 text-center flex items-center justify-center gap-1.5">
                        <Bot className="w-4 h-4" />
                      </div>
                      <div className="text-center">madonna</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="p-6">
                  <h4 className="text-lg font-medium mb-4">2. Find Convergence</h4>
                  <p className="text-muted-foreground mb-4">The goal is to pick the same word in the same round, by round 20.</p>
                  
                  <div className="grid grid-cols-2 gap-4 mt-6 bg-muted/30 p-4 rounded-md">
                    <div className="p-3 bg-card rounded shadow-sm border border-border">
                      <div className="text-xs text-muted-foreground mb-2 text-center flex items-center justify-center gap-1.5">
                        <User className="w-4 h-4" />
                      </div>
                      <div className="text-center font-medium text-primary">trampoline</div>
                    </div>
                    <div className="p-3 bg-card rounded shadow-sm border border-border">
                      <div className="text-xs text-muted-foreground mb-2 text-center flex items-center justify-center gap-1.5">
                        <Bot className="w-4 h-4" />
                      </div>
                      <div className="text-center font-medium text-primary">trampoline</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="p-6">
                  <h4 className="text-lg font-medium mb-4">3. Connect the Words</h4>
                  <p className="text-muted-foreground mb-4">Each round, pick a word that best connects the meaning of the words from the last round, without repeating 
a word.</p>
                  
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4 bg-muted/30 p-4 rounded-md">
                      <div className="col-span-2 flex justify-center">
                        <div className="px-3 py-1 rounded-full bg-muted text-xs text-muted-foreground">
                          Round 7
                        </div>
                      </div>
                      <div className="p-3 bg-card rounded shadow-sm border border-border">
                        <div className="text-xs text-muted-foreground mb-2 text-center flex items-center justify-center gap-1.5">
                          <User className="w-4 h-4" />
                        </div>
                        <div className="text-center">vacation</div>
                      </div>
                      <div className="p-3 bg-card rounded shadow-sm border border-border">
                        <div className="text-xs text-muted-foreground mb-2 text-center flex items-center justify-center gap-1.5">
                          <Bot className="w-4 h-4" />
                        </div>
                        <div className="text-center">ocean</div>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4 bg-muted/30 p-4 rounded-md">
                      <div className="col-span-2 flex justify-center">
                        <div className="px-3 py-1 rounded-full bg-muted text-xs text-muted-foreground">
                          Round 8
                        </div>
                      </div>
                      <div className="p-3 bg-card rounded shadow-sm border border-border">
                        <div className="text-xs text-muted-foreground mb-2 text-center flex items-center justify-center gap-1.5">
                          <User className="w-4 h-4" />
                        </div>
                        <div className="text-center">caribbean</div>
                      </div>
                      <div className="p-3 bg-card rounded shadow-sm border border-border">
                        <div className="text-xs text-muted-foreground mb-2 text-center flex items-center justify-center gap-1.5">
                          <Bot className="w-4 h-4" />
                        </div>
                        <div className="text-center">cruise</div>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4 bg-muted/30 p-4 rounded-md">
                      <div className="col-span-2 flex justify-center">
                        <div className="px-3 py-1 rounded-full bg-muted text-xs text-muted-foreground">
                          Round 9
                        </div>
                      </div>
                      <div className="p-3 bg-card rounded shadow-sm border border-border">
                        <div className="text-xs text-muted-foreground mb-2 text-center flex items-center justify-center gap-1.5">
                          <User className="w-4 h-4" />
                        </div>
                        <div className="text-center font-medium text-primary">royal</div>
                      </div>
                      <div className="p-3 bg-card rounded shadow-sm border border-border">
                        <div className="text-xs text-muted-foreground mb-2 text-center flex items-center justify-center gap-1.5">
                          <Bot className="w-4 h-4" />
                        </div>
                        <div className="text-center font-medium text-primary">royal</div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>}
        </div>
      </main>
      
      {!user && <Footer />}
    </div>;
}
